
import OfflineLogger from '@/utils/offlineLogger';
import { initializeNetwork } from './network/initialization';

type NetworkStatusCallback = (status: { connected: boolean }) => void;

class NetworkService {
  private listeners: NetworkStatusCallback[] = [];
  private _isOnline = true;
  
  constructor() {
    this.initialize();
  }
  
  async initialize() {
    this._isOnline = await initializeNetwork(
      this.updateOnlineStatus.bind(this),
      this.isTestEnv()
    );
  }

  private isTestEnv(): boolean {
    return typeof process !== 'undefined' && !!process.env.JEST_WORKER_ID;
  }

  
  private updateOnlineStatus(online: boolean) {
    if (online !== this._isOnline) {
      this._isOnline = online;
      OfflineLogger.info('NetworkService', `Network status updated: ${online ? 'online' : 'offline'}`);
      this.notifyListeners({ connected: online });
    }
  }

  markOffline() {
    this.updateOnlineStatus(false);
  }
  
  private notifyListeners(status: { connected: boolean }) {
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        OfflineLogger.error('NetworkService.notifyListeners', error);
      }
    });
  }
  
  addListener(callback: NetworkStatusCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }
  
  isOnline(): boolean {
    return this._isOnline;
  }

  /**
   * Force network status for test environments only.
   * This method should never be used in production code.
   */
  __setOnlineStatus(status: boolean) {
    this._isOnline = status;
  }
}

export const networkService = new NetworkService();
